import { createElement, type ComponentType } from 'react'
import { getActiveThemeSlug, isStructuralThemeSlug } from '@/themes/registry'
import { normalizeSourcePath } from './overrideMap'
import { isThemeComponent } from './isThemeComponent'
import { withThemeErrorBoundary } from './withThemeErrorBoundary'

type EagerModule = Record<string, unknown>

// Vite requires literal globs. Gate: env non-empty and != default (tree-shake for default builds).
// Runtime always filters to VITE_ACTIVE_THEME only when several theme folders exist.
const structuralEagerModules: Record<string, EagerModule> =
  import.meta.env.VITE_ACTIVE_THEME && import.meta.env.VITE_ACTIVE_THEME !== 'default'
    ? (import.meta.glob(
        [
          '../../themes/*/shared/components/**/*.tsx',
          '../../themes/*/features/*/components/**/*.tsx',
          '!../../themes/**/*.test.tsx',
          '!../../themes/**/*.spec.tsx',
        ],
        { eager: true },
      ) as Record<string, EagerModule>)
    : {}

function findThemedModule(sourcePath: string, themeSlug: string): EagerModule | undefined {
  const normalized = normalizeSourcePath(sourcePath).replace(/\\/g, '/')
  const needle = `themes/${themeSlug}/${normalized}`

  for (const [key, mod] of Object.entries(structuralEagerModules)) {
    if (key.replace(/\\/g, '/').endsWith(needle)) {
      return mod
    }
  }

  return undefined
}

function pickExport<Props extends object>(
  sourcePath: string,
  exportName: string,
  fallback: ComponentType<Props>,
): ComponentType<Props> {
  const themeSlug = getActiveThemeSlug()
  if (!isStructuralThemeSlug(themeSlug)) {
    return fallback
  }

  const mod = findThemedModule(sourcePath, themeSlug)
  const candidate = mod?.[exportName]
  if (isThemeComponent(candidate)) {
    return withThemeErrorBoundary(candidate as ComponentType<Props>, async () => fallback, sourcePath, themeSlug)
  }

  return fallback
}

/**
 * Build-time themed export for shared/feature components imported synchronously.
 * Resolution is deferred to first render to avoid circular init with eager theme globs
 * (e.g. OccupantsField → OccupantsFieldView → Calendar → this helper).
 */
export function resolveThemedExportSync<Props extends object>(
  sourcePath: string,
  exportName: string,
  fallback: ComponentType<Props>,
): ComponentType<Props> {
  let resolved: ComponentType<Props> | null = null

  function DeferredThemedExport(props: Props) {
    if (resolved === null) {
      resolved = pickExport(sourcePath, exportName, fallback)
    }
    return createElement(resolved, props)
  }

  DeferredThemedExport.displayName = `ThemedSync(${exportName})`
  return DeferredThemedExport
}
